import { computed, inject, Injectable } from "@angular/core";
import { ITaxReturnStatus } from "@app/views-interfaces/tax-return-status-model.interface";
import { getCardStatusId } from "@app/views-interfaces/card-utils";
import { JobsBillingCard } from "@app/views-interfaces/jobs-billing-card.interface";
import { BusinessLogicService } from "../business-logic";
import { JobsAndBillingJobTypesService } from "../jobs-and-billing-job-types-service/jobs-and-billing-job-types.service";
import { MappingOfStatusesService } from "../mapping-of-statuses-service";
import { ICardDragAndDropStrategy, CardState } from "./card-drag-and-drop-strategy.interface";
import { isUnassigned, UsersService } from "../users-service";

@Injectable({ providedIn: "root" })
export class JobsBillingDragAndDropStrategy implements ICardDragAndDropStrategy {
    private readonly businessLogicService = inject(BusinessLogicService);
    private readonly jobsAndBillingJobTypesService = inject(JobsAndBillingJobTypesService);
    private readonly mappingOfStatusesService = inject(MappingOfStatusesService);
    private readonly usersService = inject(UsersService);

    readonly availableJobStatusIdsByJobType = computed<Map<number, Set<string>>>(() => {
        const associations = this.jobsAndBillingJobTypesService.jobRoleStatusAssociation();
        const statusIdsByJobType = new Map<number, Set<string>>();
        if (!associations || associations.length === 0) return statusIdsByJobType;

        for (const association of associations) {
            let statusIdsSet = statusIdsByJobType.get(association.jobTypeId);
            if (!statusIdsSet) statusIdsByJobType.set(association.jobTypeId, (statusIdsSet = new Set<string>()));
            statusIdsSet.add(association.jobStatusId);
        }
        return statusIdsByJobType;
    });

    captureOriginalUserId(card: JobsBillingCard): string | null {
        return card.job?.currentlyResponsible ?? null;
    }

    captureOriginalStatusIds(card: JobsBillingCard): Set<string> {
        // J&B dashboards do not support grouped/multi-card, so only dragged card is affected
        return new Set([getCardStatusId(card)]);
    }

    isValidStatusChange(card: JobsBillingCard, statusId: string): boolean {
        const jobTypeId = card.job?.typeId;
        if (jobTypeId === null) return false;

        if (getCardStatusId(card) === statusId) return false; // Not valid if status isn't actually changing, J&B cards have no members

        return this.availableJobStatusIdsByJobType().get(jobTypeId)?.has(statusId) ?? false;
    }

    isValidAssigneeChange(card: JobsBillingCard, targetUserId: string): boolean {
        if (card.job?.currentlyResponsible === targetUserId) return false;

        if (isUnassigned(targetUserId) && !isUnassigned(card.job?.currentlyResponsible)) return true; // Allow unassigning if there's currently an assignee

        const targetUser = this.usersService.usersById()?.fromUserId.get(targetUserId) ?? this.usersService.usersById()?.fromId.get(targetUserId);
        return !!targetUser && targetUser.Active;
    }

    async onStatusDrop(card: JobsBillingCard, statusId: string): Promise<void> {
        this.businessLogicService.saveChanges(card, [{ field: "jobStatus", value: statusId }], {
            applyReturnStatusToAll: false,
            applyAssigneesToAll: false,
        });
    }

    onAssigneeDrop(card: JobsBillingCard, targetUserId: string): void {
        this.businessLogicService.saveChanges(card, [{ field: "currentlyResponsible", value: targetUserId }], {
            applyReturnStatusToAll: false,
            applyAssigneesToAll: false,
        });
    }

    getFirstValidStatusOf(state: CardState, jobType: number | null): ITaxReturnStatus | null {
        if (jobType == null) {
            return this.mappingOfStatusesService.getFirstStatusOf(state);
        }
        const associations = this.jobsAndBillingJobTypesService.jobRoleStatusAssociation();
        const statuses = this.mappingOfStatusesService.getAllStatusesOf(state);
        return statuses.find((status) => associations.some((a) => a.jobTypeId === jobType && a.jobStatusId === status?.Id)) ?? null;
    }
}
